import { AbilityBuilder, PureAbility } from "@casl/ability";
import { type UserRole } from "@prisma/client";
import { match } from "ts-pattern";
import { type permissionsCenterTabs } from "~/components/account/permissions-center/PermissionsCenterTabs";
import { value } from "~/components/helpers";
import { FiguresBaseError } from "~/lib/errors/figuresBaseError";
import { omit } from "~/lib/lodash";
import { arrayHasValues } from "~/lib/utils";
import { type FetchCompanyRolePermissionsResult } from "~/services/role-permissions/fetchCompanyRolePermissions";
import { Action, Subject, type AppAbility } from "~/services/user/permissions/caslPermissionsType";
import { resolveManagerRolePermissions } from "~/services/user/permissions/utils/resolveManagerRolePermissions";

export type Role = (typeof permissionsCenterTabs)[keyof typeof permissionsCenterTabs];

export const createPermissionsFromRolePermissions = ({
  role,
  companyRolePermissions,
  isManager,
}: {
  companyRolePermissions: FetchCompanyRolePermissionsResult;
  role: UserRole | "MANAGER";
  isManager: boolean;
}) => {
  const { can, cannot, rules } = new AbilityBuilder<AppAbility>(PureAbility);

  if (!arrayHasValues(companyRolePermissions)) {
    return rules;
  }

  const rolePermissions = companyRolePermissions?.find((rolePermission) => rolePermission.role === role);

  const existingColumns = Object.keys(omit(rolePermissions ?? {}, ["id", "companyId", "role"]));

  if (rolePermissions) {
    existingColumns.forEach((column) => {
      const subject = columnsToSubject({ column });

      const hasPermission = value(() => {
        if (isManager) {
          const managerRolePermissions = resolveManagerRolePermissions({
            rolePermissions: companyRolePermissions,
            role,
          });
          return managerRolePermissions[column as keyof typeof managerRolePermissions];
        }

        return !!rolePermissions[column as keyof typeof rolePermissions];
      });

      if (hasPermission) {
        can(Action.Access, subject);
      } else {
        cannot(Action.Access, subject);
      }
    });
  }

  return rules;
};

const columnsToSubject = ({ column }: { column: string }): Subject => {
  return match(column)
    .with("canAccessMarketData", () => Subject.MarketData)
    .with("canAccessComptryxMarketData", () => Subject.MarketData_Comptryx)
    .with("canAccessSalaryBands", () => Subject.SalaryBands)
    .with("canAccessCompensationReview", () => Subject.CompensationReview)
    .with("canAccessPeopleDashboard", () => Subject.PeopleDashboard)
    .with("canAccessPeopleDashboardMarketData", () => Subject.PeopleDashboard_MarketData)
    .with("canAccessPeopleDashboardSalaryBands", () => Subject.PeopleDashboard_SalaryBands)
    .with("canAccessPeopleDashboardComptryx", () => Subject.PeopleDashboard_Comptryx)
    .otherwise(() => {
      throw new FiguresBaseError(
        "We've found an invalid column while creating permissions from company role permissions"
      );
    });
};
